/**
 * SuggestionCard — the canonical "suggestion" surface: a quiet card with a 2px
 * brand-indigo spine and a faint indigo wash, used everywhere we surface
 * recommendations (the home/full Suggestions feed, the "Suggested agents &
 * flows" panel). One primitive so every suggestion reads identically — header
 * elsewhere, bare accent-card stack here.
 *
 * `href` makes the whole card the click target (the home pattern — no inner
 * button); without it the card is static. Compose the body as children.
 *
 * The accent uses `color-mix(...)` against `--primary`/`--card`, never the
 * invalid hex-alpha form (memory project_chart_palette).
 */

import * as React from "react";
import { RouterLink as Link } from "../router";
import { Card } from "../card";
import { cn } from "../utils";

/** 2px indigo spine + faint indigo wash. Exported for surfaces that compose
 *  the accent onto their own Card (e.g. empty states). */
export const SUGGESTION_ACCENT =
  "border-l-2 [border-left-color:color-mix(in_oklab,var(--primary)_55%,transparent)] [background-color:color-mix(in_oklab,var(--primary)_7%,var(--card))]";

export interface SuggestionCardProps {
  /** When set, the whole card is a link to this href (the home pattern). */
  href?: string;
  className?: string;
  children: React.ReactNode;
}

export function SuggestionCard({ href, className, children }: SuggestionCardProps) {
  if (href) {
    return (
      <Card
        interactive
        asChild
        className={cn(
          "transition-all hover:bg-transparent hover:ring-1 hover:ring-border",
          SUGGESTION_ACCENT,
          className,
        )}
      >
        <Link href={href}>{children}</Link>
      </Card>
    );
  }
  return <Card className={cn(SUGGESTION_ACCENT, className)}>{children}</Card>;
}
